import React from "react";
import {
  GridFilterPanel,
  useGridApiContext,
  gridFilterModelSelector,
  GridFilterPanelProps,
  GridFilterModel,
} from "@mui/x-data-grid-pro";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import CheckIcon from "@mui/icons-material/Check";

/**
 * This component is designed to function as a custom GridFilterPanel for the DataGridPro component where
 * the user must manually apply filters by clicking a button before they are persisted.
 */
export const ManualApplyGridFilterPanel: React.FC<ManualApplyGridFilterPanelProps> = (props) => {
  const apiContext = useGridApiContext();
  const lastAppliedFilterModelRef = React.useRef<GridFilterModel | null>(null);
  const hasAppliedRef = React.useRef(false);

  // Track clicks outside the panel to detect when it closes
  React.useEffect(() => {
    if (!lastAppliedFilterModelRef.current) {
      const currentFilterModel = gridFilterModelSelector(apiContext.current.state, apiContext.current.instanceId);
      lastAppliedFilterModelRef.current = JSON.parse(JSON.stringify(currentFilterModel));
    }

    const handleDocumentClick = (event: MouseEvent) => {
      // Check if click is outside the filter panel
      const filterPanel =
        document.querySelector(".MuiDataGrid-filterPanel") || document.querySelector(".MuiDataGrid-panel");

      if (filterPanel && !filterPanel.contains(event.target as Node)) {
        // Check if we've already clicked Apply
        if (!hasAppliedRef.current) {
          // Reset to last applied state
          setTimeout(() => {
            // We use setTimeout to ensure this runs after the panel close logic
            if (lastAppliedFilterModelRef.current) {
              apiContext.current.setFilterModel(lastAppliedFilterModelRef.current);
            }
          }, 100);
        }
        // Reset the applied flag for next time
        hasAppliedRef.current = false;
      }
    };

    // We also need to track the filter toggle button
    const handleFilterButtonClick = () => {
      hasAppliedRef.current = false;
    };

    // Find and monitor the filter button
    const filterButton = document.querySelector(".MuiDataGrid-toolbarFilterButton");
    if (filterButton) {
      filterButton.addEventListener("click", handleFilterButtonClick);
    }

    document.addEventListener("mousedown", handleDocumentClick);

    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);
      if (filterButton) {
        filterButton.removeEventListener("click", handleFilterButtonClick);
      }
    };
  }, [apiContext]);

  const handleApplyClick = () => {
    // Get current filter model
    const filterModel = gridFilterModelSelector(apiContext.current.state, apiContext.current.instanceId);

    // Save as last applied and mark as applied
    lastAppliedFilterModelRef.current = JSON.parse(JSON.stringify(filterModel));
    hasAppliedRef.current = true;

    // Call the handler from props
    props.onApplyFilters(filterModel);

    // Close the filter panel
    apiContext.current.hideFilterPanel();
  };

  return (
    <div>
      <GridFilterPanel {...props} />
      <Box sx={{ p: 1, display: "flex", justifyContent: "flex-end" }}>
        <Button color="primary" variant="contained" startIcon={<CheckIcon />} onClick={handleApplyClick} size="small">
          Apply Filters
        </Button>
      </Box>
    </div>
  );
};

export interface ManualApplyGridFilterPanelProps extends GridFilterPanelProps {
  onApplyFilters: (filterModel: GridFilterModel) => void;
}

export default ManualApplyGridFilterPanel;
